import React, { useState, useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { MessageSquare, ShoppingBag, ScanLine, Pill, ArrowUpRight, Stethoscope, ClipboardPlus, Mail, FolderOpen, Plane, LayoutDashboard } from "lucide-react";
import ReferralPadModal from "../components/ReferralPadModal";
import PadRequestModal from "../components/PadRequestModal";
import { LOGO_URL, BRAND, BRAND_ORANGE, PRACTICE } from "../lib/brand";
import { useAuth } from "../lib/auth";

const modules = [
  // GCOMS.ai temporarily hidden from the public Landing while reasoning
  // is being seeded — re-enable by uncommenting.
  // { name: "GCOMS.ai", tag: "AI Case Consult", color: "sage", deep: "sage-deep", icon: MessageSquare, blurb: "Ask any case question and get answers reasoned the way Dr. Yunov would handle it — trained on years of clinical decision-making." },
  { name: "Coverage", tag: "Vacation Coverage", color: "sky", deep: "sky-deep", icon: Plane, blurb: "Need emergency coverage while you're away? Submit dates and scope; we route it through the network." },
  { name: "Doc.Center", tag: "Documents & Forms", color: "terracotta", deep: "terracotta-deep", icon: FolderOpen, blurb: "Patient handouts, consent forms, financials, and CE — organized in one searchable library." },
  { name: "Drugs.rx", tag: "Dental Pharmacology", color: "moss", deep: "moss-deep", icon: Pill, blurb: "Every drug used in dentistry — indications, key facts, and formulations — searchable in seconds." },
];

const accentBg = {
  sage: "bg-sage/15", terracotta: "bg-terracotta/15", sky: "bg-sky/15", moss: "bg-moss/15",
};
const accentText = {
  "sage-deep": "text-sage-deep", "terracotta-deep": "text-terracotta-deep",
  "sky-deep": "text-sky-deep", "moss-deep": "text-moss-deep",
};

export default function Landing() {
  const [referOpen, setReferOpen] = useState(false);
  const [padsOpen, setPadsOpen] = useState(false);
  const { user } = useAuth();
  const [params] = useSearchParams();

  // First thing a new visitor sees: the Refer-a-Patient popup. Auto-opens
  // once per browser session for non-logged-in visitors; fully closable.
  useEffect(() => {
    if (!user && sessionStorage.getItem("gcoms_refer_popup_shown") !== "1") {
      sessionStorage.setItem("gcoms_refer_popup_shown", "1");
      setReferOpen(true);
    }
  }, [user]);

  // Logged-in users land straight on /dashboard unless they explicitly
  // opted to view the public site (the "View public site" link in the
  // app sidebar passes ?home=1).
  if (user && params.get("home") !== "1") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-bone text-ink">
      <ReferralPadModal open={referOpen} onClose={() => setReferOpen(false)} />
      <PadRequestModal open={padsOpen} onClose={() => setPadsOpen(false)} />
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img src={LOGO_URL} alt="GCOMS" className="w-10 h-10 rounded-full bg-black p-0.5" />
            <div>
              <div className="font-heading text-lg font-bold tracking-tight leading-none">{BRAND}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">Referral Network</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#modules" className="hover:text-ink transition-colors">Network</a>
            <a href="#refer" className="hover:text-ink transition-colors">Refer a patient</a>
            <a href="#access" className="hover:text-ink transition-colors">Access</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Link
                to="/dashboard"
                data-testid="landing-dashboard-link"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 transition-colors"
                style={{ backgroundColor: BRAND_ORANGE }}
              >
                <LayoutDashboard size={14} /> Go to dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login-link" className="text-sm text-muted-foreground hover:text-ink">Sign in</Link>
                <Link to="/signup" data-testid="landing-signup-link" className="px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 transition-colors" style={{ backgroundColor: BRAND_ORANGE }}>
                  Join the network
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Slim Refer-a-Patient ribbon — top of page, secondary to the hero */}
      <section id="refer" className="border-b border-black/5" style={{ backgroundColor: "#FDE6D8" }} data-testid="refer-ribbon">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <ClipboardPlus size={16} style={{ color: BRAND_ORANGE }} className="shrink-0" />
            <span className="text-sm text-ink/80 truncate">
              <span className="font-medium text-ink">Refer a patient</span>
              <span className="hidden sm:inline"> — sent directly to <span className="font-mono">info@goldcoastoms.com</span></span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="hero-refer-patient"
              onClick={() => setReferOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-white text-xs font-medium hover:opacity-90"
              style={{ backgroundColor: BRAND_ORANGE }}
            >
              <ClipboardPlus size={13} /> Refer a patient
            </button>
            <button
              data-testid="hero-request-pads"
              onClick={() => setPadsOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md border text-xs font-medium hover:bg-white"
              style={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE }}
            >
              <Mail size={13} /> Request pads
            </button>
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="bg-grain">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="uppercase-label mb-5">Referral Network · Members Only</div>
            <h1 className="font-heading text-5xl lg:text-7xl font-bold tracking-tight leading-[0.95]">
              The tools <br />
              our referrals <br />
              actually need.
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              An invite-first network for referring doctors — case reasoning, wholesale sourcing,
              implant identification, and dental pharmacology, built around one practice's playbook.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="hero-cta-signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-ink text-bone font-medium hover:bg-ink/90 transition-all">
                Request access <ArrowUpRight size={16} />
              </Link>
              <Link to="/login" data-testid="hero-cta-login" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-ink/15 hover:bg-white transition-colors">
                Sign in
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-2"><Stethoscope size={14} /> Licensed clinicians only</div>
              <div>Free for referring doctors · or $99/mo</div>
            </div>
          </div>

          {/* Bento preview */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {modules.map((m, i) => (
              <div
                key={m.name}
                className="module-card p-5 animate-fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className={`w-9 h-9 rounded-md ${accentBg[m.color]} flex items-center justify-center mb-3`}>
                  <m.icon size={18} className={accentText[m.deep]} />
                </div>
                <div className="font-heading text-base font-semibold">{m.name}</div>
                <div className="uppercase-label mt-1">{m.tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules detail */}
      <section id="modules" className="border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="uppercase-label mb-3">The Modules</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight max-w-2xl">
            Built to remove friction from referring practices.
          </h2>
          <div className="grid md:grid-cols-2 gap-5 mt-14">
            {modules.map((m) => (
              <div key={m.name} className="module-card p-8" data-testid={`module-card-${m.name.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                <div className="flex items-start gap-5">
                  <div className={`w-12 h-12 rounded-md ${accentBg[m.color]} flex items-center justify-center shrink-0`}>
                    <m.icon size={22} className={accentText[m.deep]} />
                  </div>
                  <div>
                    <div className="font-heading text-2xl font-semibold">{m.name}</div>
                    <div className="uppercase-label mt-1">{m.tag}</div>
                    <p className="mt-4 text-muted-foreground leading-relaxed">{m.blurb}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="uppercase-label mb-3">How it works</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight max-w-2xl">Three steps to access the network.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {[
              { n: "01", t: "Apply", d: "Sign up with your practice details. We verify licensing before activation." },
              { n: "02", t: "Activate", d: "Active referring doctors get access automatically — no codes to remember. Otherwise subscribe at $99/mo." },
              { n: "03", t: "Use it", d: "Request vacation coverage, browse documents, and look up dental drugs — everything in one place." },
            ].map((s) => (
              <div key={s.n} className="module-card p-8">
                <div className="font-mono text-xs text-sage-deep">{s.n}</div>
                <div className="font-heading text-2xl font-semibold mt-3">{s.t}</div>
                <p className="mt-3 text-muted-foreground leading-relaxed text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Access pricing */}
      <section id="access" className="border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-6">
          <div className="module-card p-10 bg-ink text-bone">
            <div className="uppercase-label text-bone/60">For referring doctors</div>
            <div className="font-heading text-4xl font-bold mt-4">Free with active referrals</div>
            <p className="mt-4 text-bone/70 leading-relaxed">If you're an active referring doctor, we activate your account on our end — no codes, no expirations to track. Just sign up and email us.</p>
            <Link to="/signup" data-testid="access-referrer-cta" className="inline-flex items-center gap-2 mt-8 px-5 py-3 rounded-md bg-bone text-ink font-medium hover:bg-bone/90 transition-colors">
              Request access <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="module-card p-10">
            <div className="uppercase-label">For everyone else</div>
            <div className="font-heading text-4xl font-bold mt-4">$99<span className="text-lg text-muted-foreground font-medium">/month</span></div>
            <p className="mt-4 text-muted-foreground leading-relaxed">Not a referring doctor yet? Subscribe monthly — cancel any time. Renews every 30 days.</p>
            <Link to="/signup" data-testid="access-subscribe-cta" className="inline-flex items-center gap-2 mt-8 px-5 py-3 rounded-md border border-ink/15 hover:bg-bone transition-colors font-medium">
              Subscribe to join <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/5 py-10 px-6 text-sm text-muted-foreground">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>© 2026 {BRAND} · {PRACTICE}</div>
          <div className="uppercase-label">For licensed dental professionals only</div>
        </div>
      </footer>
    </div>
  );
}
